/* eslint-disable react-hooks/exhaustive-deps */
import L from 'leaflet';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AimOutlined, DownOutlined, MinusOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import { camelCaseToTitle as camelCaseToSentenceCase } from '../../helpers/StringHelper';
import { maxBounds } from '../../constants/map';
import { Link } from 'react-router-dom';
import Truncate from '../truncate';
import { Typography, Tag } from 'antd';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { getApplication } from '../../services/BaseService';
import { useApp } from '../../helpers/user';

const { Text } = Typography;

const _app = getApplication();
export const SET_VIEW_DEFAULT_ZOOM = 3;
export const MAP_TOKEN = _app === 'tazama' ?
  'pk.eyJ1IjoicmVkaXM5OTkiLCJhIjoiY2x4YWV5MzA5MmtuZzJpcXM5Y201Z2E2YiJ9.m5bwPg-Tj4Akesl1yQUa3w'
  : 'pk.eyJ1IjoiZmxha2EiLCJhIjoiY2tjeXlodjhzMDFxeDJ0cW16Z3ZvZHl3dyJ9.C0ZHWKXYRbtcFDswx4hwCg';
export const TILE_LAYER_URL = _app === 'tazama' ?
  'https://api.mapbox.com/styles/v1/redis999/clxag3qlh02be01qr8qlv0p1x/tiles/256/{z}/{x}/{y}@2x?access_token={access_token}'
  : 'https://api.mapbox.com/styles/v1/flaka/ckpxwobs70or417o5m7z5occy/tiles/256/{z}/{x}/{y}@2x?access_token={access_token}';

export const useMapConfig = () => {
  const app = useApp();
  return useMemo(() => ({
    MAP_TOKEN: app === 'tazama' ?
      'pk.eyJ1IjoicmVkaXM5OTkiLCJhIjoiY2x4YWV5MzA5MmtuZzJpcXM5Y201Z2E2YiJ9.m5bwPg-Tj4Akesl1yQUa3w'
      : 'pk.eyJ1IjoiZmxha2EiLCJhIjoiY2tjeXlodjhzMDFxeDJ0cW16Z3ZvZHl3dyJ9.C0ZHWKXYRbtcFDswx4hwCg',
    TILE_LAYER_URL: app === 'tazama' ?
      'https://api.mapbox.com/styles/v1/redis999/clxag3qlh02be01qr8qlv0p1x/tiles/256/{z}/{x}/{y}@2x?access_token={access_token}'
      : 'https://api.mapbox.com/styles/v1/flaka/ckpxwobs70or417o5m7z5occy/tiles/256/{z}/{x}/{y}@2x?access_token={access_token}',
  }), [app]);
};

export const defaultMapConfig = {
  maxZoom: 18,
  preferCanvas: true,
  zoomControl: false,
  maxBounds: maxBounds,
};

export function useMapHelper({ mapRef, mapCenter, allData }) {
  const [mapMarkers, setMapMarkers] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);
  const getZoom = useCallback((mapRef) => mapRef.getZoom() > SET_VIEW_DEFAULT_ZOOM ? mapRef.getZoom() : SET_VIEW_DEFAULT_ZOOM, []);
  const { t } = useTranslation();

  // Icon URLs for each type
  const { current: iconsConfig } = useRef({
    network: {
      lir: 'lir-icon',
      ellipse: 'ellipse-icon',
    },
    datachain: {
      datachain: 'datachain-icon',
    },
    location: {
      mineSite: 'location minesites-location',
      village: 'location villages-location',
      other: 'location other-location',
    },
    event: {
      incident: 'incident',
      other: 'other-events',
    },
  });

  // Recenter Map
  const recenterMap = useCallback(() => {
    if (mapRef) {
      if (allData && allData.length) {
        const pos = allData.map(m => [Number(m.marker.lat), Number(m.marker.lng)]);
        const bounds = new L.LatLngBounds(pos);
        mapRef.fitBounds(bounds);
      } else {
        mapRef.setView(mapCenter, SET_VIEW_DEFAULT_ZOOM);
      }
    }
  }, [mapRef, mapCenter, allData]);

  // Actions to do when active marker is changed
  useEffect(() => {
    if (mapRef) {
      if (activeMarker) {
        mapRef.eachLayer((layer) => {
          if (layer.options.icon) {
            if (layer._leaflet_id === activeMarker) {
              layer.openPopup();
              layer.off('popupclose');
              layer.on('popupclose', () => {
                setActiveMarker(null);
              })
            } else {
              layer.off('click');
              layer.on('click', () => {
                setActiveMarker(layer._leaflet_id);
              });
            }
          }
        });
      } else {
        mapRef.eachLayer((layer) => {
          if (layer.options.icon) {
            const icon = layer.getIcon();
            layer.off('click');
            layer.on('click', () => {
              setActiveMarker(layer._leaflet_id)
            });
            layer.setIcon(icon);

            if (layer.getPopup() && layer.getPopup().isOpen()) {
              layer.closePopup();
            }
          }
        });
      }
    }
  }, [activeMarker, mapRef]);

  // Draws the marker in the map
  const addIconToMap = useCallback((coordinates, className, groupName, name, iconSize, html) => {
    if (mapRef) {
      const marker = L.marker(coordinates, {
        icon: L.divIcon({
          html,
          className: `map-marker ${className}`,
          iconSize,
        }),
        zIndexOffset: parseInt(iconSize[0]),
      })
        .addTo(mapRef);
      if (name) {
        marker.bindPopup(name);
      }
      marker.on('click', () => {
        setActiveMarker(marker._leaflet_id);
      });
      return marker;
    }
    return null;
  }, [mapRef]);

  const renderPopover = (header, config) => {
    const listItems = config.map((c) => `
      <div class="item">
        <div class="main">
          ${c.image ? `<img src="${c.image}" />` : ''}
          <span>${i18next.t(c.name)}</span>
        </div>
        <div class="value">
          <span>${c.value}</span>
        </div>
      </div>
    `);
    return `
      <div class="custom-popover">
        <h5>${header}</h5>
        <div class="list">
          ${listItems.join('')}
        </div>
      </div>
    `;
  };

  const getRiskConfig = (risk) => {
    let secondaryCL;
    let color;

    switch (risk) {
      case '0':
        secondaryCL = 'none';
        color = '#f1f1f1';
        break;
      case '1':
        secondaryCL = 'veryLow';
        color = '#73D13D';
        break;
      case '2':
        secondaryCL = 'low';
        color = '#FADB14';
        break;
      case '3':
        secondaryCL = 'medium';
        color = '#FA8C16';
        break;
      case '4':
        secondaryCL = 'high';
        color = '#FF4D4F';
        break;
      case '5':
        secondaryCL = 'veryHigh';
        color = '#CF1322';
        break;
      default:
    }
    return { secondaryCL, color };
  };

  // Draws the marker in the map and saves it in mapMarkers state
  const addIconToMapInitialy = useCallback((coordinates, groupName, markerName, data, maxTotal) => {
    if (mapRef) {
      let marker;
      let iconClassName = '';
      let innerHtml = '';
      let iconSize = [20, 20];
      if (groupName === 'risk') {
        let score = data.data ? data.data.score ? data.data.score.toFixed(1) : 0.0 : 0.0;
        const strScore = score.toString();
        const split = strScore.split('.');
        if (split[1] === '0') {
          score = parseInt(split[0]);
        }
        const { color, secondaryCL } = getRiskConfig(split[0]);

        iconClassName = `risk ${secondaryCL}`;
        const name = data.data ? renderPopover(
          data.data.name,
          [
            {
              name: t("Aggregate Risk Score"),
              value: `<span style="color: ${color}">${t(camelCaseToSentenceCase(secondaryCL))} ${score}</span>`,
            },
            {
              name: t("Assessed Categories"),
              value: data.data.assessedRiskTypes,
            },
            {
              name: t("Assessed Risks"),
              value: data.data.assessedRiskSubTypes,
            }
          ]
        ) : '';
        innerHtml = `<div class="main"><span>${score || 0}</span></div>`;
        const perc = score / maxTotal;
        const size = 60 * perc;
        if (size > 29) {
          iconSize = [size, size];
        } else {
          iconSize = [29, 29];
        }
        marker = addIconToMap(coordinates, iconClassName, groupName, name, iconSize, innerHtml);
      } else {
        if (data.data) {
          let name = 'Name';
          if (groupName === 'event') {
            iconClassName = iconsConfig['event']['incident'];
            const totals = { major: 0, minor: 0, moderate: 0, total: data.data.length };
            totals.minor = (data ? (data.data || []) : []).filter((d) => d.type === 'minor_incident').length;
            totals.major = (data ? (data.data || []) : []).filter((d) => d.type === 'major_incident').length;
            totals.moderate = (data ? (data.data || []) : []).filter((d) => d.type === 'moderate_incident').length;
            let background = '#f1f1f1';
            const majorPerc = totals.major / totals.total * 100;
            const moderatePerc = totals.moderate / totals.total * 100;

            if (totals.major && totals.minor && totals.moderate) {
              background = `radial-gradient(circle, #cf1322 0%, #fa8c16 ${majorPerc}%, #fadb14 ${moderatePerc + majorPerc}%);`;
            } else if (totals.major && totals.minor) {
              background = majorPerc < 50 ?
                `radial-gradient(circle, #cf1322 0%, #fadb16 ${majorPerc}%);` :
                `radial-gradient(circle, #cf1322 ${majorPerc}%, #fadb16 100%);`;
            } else if (totals.major && totals.moderate) {
              background = majorPerc < 50 ?
                `radial-gradient(circle, #cf1322 0%, #fa8c16 ${majorPerc}%);` :
                `radial-gradient(circle, #cf1322 ${majorPerc}%, #fa8c16 100%);`;
            } else if (totals.major) {
              background = '#cf1322';
            } else if (totals.minor && totals.moderate) {
              background = moderatePerc < 50 ?
                `radial-gradient(circle, #fa8c16 0%, #fadb16 ${moderatePerc}%);` :
                `radial-gradient(circle, #fa8c16 ${moderatePerc}%, #fadb16 100%);`;
            } else if (totals.minor) {
              background = '#fadb14';
            } else if (totals.moderate) {
              background = '#fa8c16 ';
            }

            name = renderPopover(data.data[0] ? data.data[0].locationName : '', [
              {
                name: 'Major Incidents',
                image: '/assets/images/dashboard/map-icons/major-incident-events-icon.svg',
                value: totals.major,
              },
              {
                name: 'Moderate Incidents',
                image: '/assets/images/dashboard/map-icons/moderate-incident-events-icon.svg',
                value: totals.moderate,
              },
              {
                name: 'Minor Incidents',
                image: '/assets/images/dashboard/map-icons/minor-incident-events-icon.svg',
                value: totals.minor,
              }
            ]);

            innerHtml = `
            <div class="main" style="background: ${background}; border: 2px solid white;">
            <span>${data.total || 0}</span>
            </div>`;
          } else if (groupName === 'datachain') {
            iconClassName = 'datachain-icon';
            name = renderPopover(data.data[0] ? data.data[0].locationName : '', [
              {
                name: 'Dashboard::Individual',
                value: (data ? (data.data || []) : []).filter((d) => d.type === 'individual').length,
              },
              {
                name: 'Dashboard::Business Entity',
                value: (data ? (data.data || []) : []).filter((d) => d.type === 'buisnessEntity').length,
              },
              {
                name: 'Dashboard::Civil society',
                value: (data ? (data.data || []) : []).filter((d) => d.type === 'civilSociety').length,
              }
            ]);
            innerHtml = `<div class="main-c">${data.total || 0}</div>`;
          }
          const base = groupName === 'event' ? 30 : 25;
          const mp = groupName === 'event' ? 1.3 : 1;
          const perc = data.total / maxTotal * mp;
          const size = 50 * perc;
          if (size > base) {
            iconSize = [size, size];
          } else {
            iconSize = [base, base];
          }
          marker = addIconToMap(coordinates, iconClassName, groupName, name, iconSize, innerHtml);
        } else {
          const group = iconsConfig[groupName];
          iconClassName = group ? group[markerName] || '' : '';
          innerHtml = '<div class="main"></div>';
          marker = addIconToMap(coordinates, iconClassName, groupName, `<strong>${data.name}</strong>`, iconSize, innerHtml);
        }
      }

      if (marker) {
        setMapMarkers((prevMapMarkers) => [...prevMapMarkers, {
          id: marker._leaflet_id,
          coordinates,
          groupName,
          markerName,
          iconClassName,
          data,
        }]);
      }
    }
  }, [mapRef]);

  // Map control handlers
  const zoomHandler = useCallback(() => mapRef.setZoom(mapRef.getZoom() + 1), [mapRef]);
  const zoomOutHandler = useCallback(() => mapRef.setZoom(mapRef.getZoom() - 1), [mapRef]);

  const moveUpHandler = useCallback(() => {
    const center = mapRef.getCenter();
    const value = 30 / mapRef.getZoom();
    mapRef.panTo(new L.LatLng(center.lat + value, center.lng));
  }, [mapRef]);

  const moveDownHandler = useCallback(() => {
    const center = mapRef.getCenter();
    const value = 30 / mapRef.getZoom();
    mapRef.panTo(new L.LatLng(center.lat - value, center.lng));
  }, [mapRef]);

  const clearMapMarkers = useCallback(() => {
    if (mapRef) {
      mapRef.eachLayer((layer) => {
        if (layer._icon) {
          mapRef.removeLayer(layer);
        }
      })
      setMapMarkers([]);
    }
  }, [mapRef]);

  const mapOptionsButtonsConfig = useMemo(() => [
    {
      icon: <PlusOutlined style={{ color: '#727272' }} />,
      handler: zoomHandler,
    },
    {
      icon: <MinusOutlined style={{ color: '#727272' }} />,
      handler: zoomOutHandler,
    },
    {
      icon: <AimOutlined style={{ color: '#727272' }} />,
      handler: recenterMap,
    },
    {
      icon: <UpOutlined style={{ color: '#727272' }} />,
      handler: moveUpHandler,
    },
    {
      icon: <DownOutlined style={{ color: '#727272' }} />,
      handler: moveDownHandler,
    },
  ], [zoomHandler, moveUpHandler, moveDownHandler, zoomOutHandler, recenterMap]);

  return {
    addIconToMapInitialy,
    activeMarker,
    mapOptionsButtonsConfig,
    setActiveMarker,
    mapMarkers,
    clearMapMarkers,
    getRiskConfig,
    getZoom,
  };
}

const getIncidentConfig = (name) => {
  switch (name) {
    case 'moderate_incident':
      return { label: 'Moderate', color: 'orange' };
    case 'minor_incident':
      return { label: 'Minor', color: 'gold' };
    case 'major_incident':
      return { label: 'Major', color: 'red' };
    default:
      return { label: '', color: '' };
  }
};


export const getIncidentsTableColumns = (t, module) => [
  {
    title: t('Name'),
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (name, record) => (
      <Link target="_blank" rel="noopener noreferrer" to={`/app/${module}/view/event/${record.id}`}>
        <Truncate
          maxChar={20}
          text={name}
          className="ellipsis-span name-span text-light-blue">
        </Truncate>
      </Link>
    ),
  },
  {
    title: t('Type'),
    dataIndex: 'type',
    key: 'type',
    sorter: (a, b) => a.name.localeCompare(b.type),
    render: (name) => {
      const config = getIncidentConfig(name);
      return <Tag style={{ width: 70, textAlign: 'center' }} color={config.color}>{t(config.label)}</Tag>
    }
  },
];

export const getDatachainTableColumns = (t, module) => [
  {
    title: t('Name'),
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (name, record) => (
      <Link target="_blank" rel="noopener noreferrer" to={`/app/${module}/view/lir/${record.id}?sourceId=${record.sourceId}`}>
        <Text
          style={{ width: 120 }}
          ellipsis={{ tooltip: name }}
          className="ellipsis-span name-span text-light-blue"
        >
          {name}
        </Text>
      </Link>
    ),
  },
  {
    title: t('Type'),
    dataIndex: 'type',
    key: 'type',
    sorter: (a, b) => a.type.localeCompare(b.type),
    render: (type) => {
      return (
        <Text
          style={{ width: 70 }}
          ellipsis={{ tooltip: t(camelCaseToSentenceCase(type)) }}
          className="ellipsis-span"
        >
          {t(camelCaseToSentenceCase(type))}
        </Text>
      );
    }
  },
];

export const getRiskSubTypesTableColumns = (t, module, getRiskConfig) => [
  {
    title: t('Name'),
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (name, record) => (
      <Link target="_blank" rel="noopener noreferrer" to={`/app/${module}/view/scl/${record.locationId}/risk/${record.tab}/${record.key}?sourceId=${record.sourceId}`}>
        <Text
          style={{ width: 120 }}
          ellipsis={{ tooltip: name }}
          className="ellipsis-span name-span text-light-blue"
        >
          {name}
        </Text>
      </Link>
    ),
  },
  {
    title: t('Score'),
    dataIndex: 'score',
    sorter: (a, b) => a.score - b.score,
    key: 'score',
    render: (score) => {
      const config = getRiskConfig(score.toString().split('.')[0]);
      return <p
        style={{ textAlign: 'center', color: config.color, marginBottom: 0, }}
      >{score}</p>
    }
  },
];

export const removeFiveToCoord = (coordinates = { lat: 0, lng: 0 }) => ({
  lat: coordinates.lat,
  lng: coordinates.lng,
});

export const getBounds = ({ scl, village, town }) => {
  const bounds = [];
  if (scl && scl.coordinates)
    bounds.push(removeFiveToCoord(scl.coordinates));
  if (village && village.coordinates)
    bounds.push(removeFiveToCoord(village.coordinates));
  if (town && town.coordinates)
    bounds.push(town.coordinates);
  return bounds;
};

export const notEqualBounds = (first, last) => {
  let isValid = true;
  first.forEach(({ lat, lng }, i) => {
    const l = last[i];
    if (l) {
      if (lat !== l.lat || lng !== l.lng) {
        isValid = false;
        return;
      }
    } else {
      isValid = false;
      return;
    }
  });
  return !isValid;
};

export const defaultFilters = { period: 'year' };
